/** Base dictionary (source of truth for keys). `es.ts` must mirror every key — see parity.ts. */
export const en = {
  app: {
    name: 'Sastrex Intake',
    tagline: 'systematic mastery for the AI era',
    loading: 'Loading…',
    skipToContent: 'Skip to content',
  },
  nav: {
    groups: { files: 'Documents', organization: 'Organization', admin: 'Sastrex admin' },
    fileSync: 'File Sync',
    workspaces: 'Workspaces',
    adminOrgs: 'Organizations',
    adminFiles: 'File Sync',
    adminProjects: 'Projects',
    help: 'Help',
    orgSettings: 'Organization settings',
    signOut: 'Sign out',
    openMenu: 'Open navigation',
    closeMenu: 'Close navigation',
    theme: { label: 'Theme', system: 'System', light: 'Light', dark: 'Dark' },
    identity: '{{env}} · v{{version}}',
  },
  orgGate: {
    title: 'Choose an organization',
    body: 'Documents are shared on behalf of an organization. Pick the one you are sending for.',
    none: 'You are not a member of any organization yet. Ask your Sastrex contact for an invitation, then sign in again.',
  },
  config: {
    title: 'This deployment is not configured',
    body: 'The app could not read its runtime configuration. If you operate this deployment, check the values below.',
    issues: 'Problems found',
  },
  errors: {
    generic: 'Something went wrong. Try again.',
    reference: 'Reference: {{id}}',
    retry: 'Try again',
    forbidden: 'You do not have access to this page.',
    noAccessTitle: 'Nothing to do here yet',
    noAccessBody: 'Your account has no tasks in this organization. Switch organization, or ask your administrator for access.',
    notFoundTitle: 'Page not found',
    notFoundBody: 'The address may be mistyped, or the page has moved.',
    backHome: 'Go to start',
    code: {
      network_error: 'Could not reach the server. Check your connection.',
      no_active_org: 'Select an organization first.',
      org_suspended: 'This organization is suspended. Contact Sastrex.',
      insufficient_scope: 'This credential is missing a required permission.',
      rate_limited: 'Too many requests. Wait a moment and try again.',
      validation_failed: 'Some values are not valid.',
      invalid_token: 'Your session expired. Sign in again.',
      missing_token: 'Your session expired. Sign in again.',
      not_found: 'Not found.',
      forbidden: 'You do not have permission to do this.',
      internal_error: 'The server hit an error. Try again.',
      org_not_provisioned: 'This organization was not set up by Sastrex. Choose another organization or contact Sastrex.',
      no_tenant_access: 'You are not assigned to any tenant yet. Ask your organization admin to add you to one.',
      tenant_required: 'Choose a tenant first.',
      tenant_not_active: 'This tenant is not active. Choose another tenant.',
      platform_org_protected: 'The Sastrex organization cannot be changed here.',
      provisioning_failed: 'This organization could not be created. Check the details and try again.',
      tenant_archived: 'This workspace is archived. Its documents are kept; ask Sastrex to restore it.',
      tenant_limit_reached: 'Your organization has reached its workspace limit.',
      tenant_unavailable: 'This workspace’s storage did not answer. Try again in a moment.',
      admin_invite_failed: 'The administrator invitation was refused. Check the email address and try again.',
      idempotency_key_reused: 'This request was already sent with different details. Try again.',
    },
  },
  common: {
    cancel: 'Cancel',
    close: 'Close',
    copy: 'Copy',
    copied: 'Copied',
    loadMore: 'Load more',
    search: 'Search',
    all: 'All',
    revoke: 'Revoke',
    create: 'Create',
    download: 'Download',
    never: 'Never',
    remove: 'Remove',
    optional: 'Optional',
  },
  status: {
    upload: {
      PENDING: 'Pending',
      UPLOADED: 'Received',
      REJECTED: 'Rejected',
      EXPIRED: 'Expired',
      DELETED: 'Deleted',
    },
    channel: { WEB: 'Web', API: 'API', SFTP: 'SFTP' },
    org: { ACTIVE: 'Active', SUSPENDED: 'Suspended' },
    tenant: { PROVISIONING: 'Setting up', ACTIVE: 'Active', FAILED: 'Setup failed', DEPROVISIONING: 'Archiving', ARCHIVED: 'Archived' },
    item: {
      queued: 'Ready',
      hashing: 'Preparing',
      registering: 'Preparing',
      uploading: 'Uploading',
      completing: 'Verifying',
      uploaded: 'Received',
      rejected: 'Rejected',
      failed: 'Failed',
      cancelled: 'Cancelled',
      skipped: 'Not sent',
    },
  },
  fileSync: {
    title: 'File Sync',
    subtitle: 'Send documents to Sastrex by upload, API or SFTP, and follow every file in one place.',
    tabsLabel: 'Ways to send files',
    tabs: { upload: 'Upload', api: 'API', sftp: 'SFTP' },
    files: 'Files',
    filesIntro: 'Everything received through any channel, from every workspace you can see. Select a file to preview it.',
    search: 'Search by file name',
    activeFilters: 'Active filters',
    removeFilter: 'Remove the {{column}} filter',
    clearAll: 'Clear all',
    columns: { file: 'File', folder: 'Folder', batch: 'Batch', organization: 'Organization', workspace: 'Workspace', channel: 'Channel', status: 'Status', fulfilment: 'Fulfilment', size: 'Size', received: 'Received', by: 'Sent by' },
    folderStartsWith: 'Folder starts with',
    noBatches: 'No batches yet',
    batchesFailed: 'Batches could not be loaded.',
    batchFilterUnavailable: 'Choose an organization to filter by batch',
    bulk: {
      label: 'Selected files',
      selectAll: 'Select all files shown',
      select: 'Select {{name}}',
      selected_one: '{{formatted}} file selected',
      selected_other: '{{formatted}} files selected',
      progress: 'Downloading {{done}} of {{total}}…',
      download: 'Download as ZIP',
      clear: 'Clear selection',
      max: 'Up to {{count}} files at a time.',
      saved_one: '{{count}} file saved',
      saved_other: '{{count}} files saved',
      skipped_one: '{{count}} file could not be included',
      skipped_other: '{{count}} files could not be included',
      tooLarge: 'Too large to zip in this browser (over 1 GB). Use Chrome or Edge, or select fewer files.',
      failed: 'The download stopped. Try again.',
      delete: 'Delete',
      deleting: 'Deleting…',
      deleted_one: '{{count}} file deleted',
      deleted_other: '{{count}} files deleted',
      notDeleted_one: '{{formatted}} file could not be deleted. It stays selected: try again, or open it to see why.',
      notDeleted_other: '{{formatted}} files could not be deleted. They stay selected: try again, or open them to see why.',
      deleteConfirm: {
        title_one: 'Delete {{count}} file?',
        title_other: 'Delete {{count}} files?',
        body: 'They are removed from storage for everyone in your organization. This cannot be undone.',
        confirm: 'Delete',
      },
    },
    actions: {
      more: 'More actions',
      rename: 'Rename',
      delete: 'Delete',
      deleteConfirm: { title: 'Delete “{{name}}”?', body: 'It is removed from storage for everyone in your organization. This cannot be undone.', confirm: 'Delete file' },
      deleteFailed: 'The file could not be deleted: {{reason}}',
      remove: 'Remove',
      removeConfirm: { title: 'Remove “{{name}}”?', body: 'This file never arrived, so there is nothing stored. Its entry is removed from the list for everyone in your organization.', confirm: 'Remove entry' },
    },
    rename: {
      title: 'Rename “{{name}}”',
      name: 'New name',
      hint: 'Changes how the file is shown and downloaded. The stored file is not touched.',
      submit: 'Rename',
      invalid: 'Use 1 to 255 characters, without slashes or control characters.',
      toast: 'File renamed',
    },
    admin: {
      title: 'File Sync',
      subtitle: 'Choose a client and workspace in the top bar to find their files, or send files to them the same ways they do.',
      organization: 'Organization',
      allOrganizations: 'All organizations',
      orgSwitch: 'Client organization: {{name}}. Change',
      pickOrg: 'Choose an organization to send files, issue API keys or set up SFTP for it. With all organizations you can browse, preview and download.',
      suspended: 'This organization is suspended: its files can be viewed and downloaded, but nothing can be sent.',
      acting: 'You are acting for {{org}}. What you send or set up is visible to them and recorded with your name.',
    },
    filter: { label: 'Filter by {{column}}', labelActive: 'Filter by {{column}} (active)', apply: 'Apply', clear: 'Clear' },
    groups: { documents: 'PDF', images: 'Images', office: 'Office documents', data: 'Data and text', archives: 'Archives' },
    any: 'Any',
    minMb: 'Min (MB)',
    maxMb: 'Max (MB)',
    from: 'From',
    to: 'To',
    byContains: 'Name, email or key contains',
    emptyTitle: 'No files yet',
    emptyBody: 'Files you send by upload, API or SFTP appear here.',
    noMatches: 'No files match these filters.',
    shown_one: '{{n}} file',
    shown_other: '{{n}} files',
    shownMore_one: '{{n}} file shown, more available',
    shownMore_other: '{{n}} files shown, more available',
    preview: {
      loading: 'Loading…',
      close: 'Close preview',
      of: 'Preview of {{name}}',
      csv: 'File contents',
      notPreviewable: 'No preview for this type. Download it to open it.',
      notReceived: 'Only received files can be previewed.',
      missing: 'This file is not available. It may have been deleted, or it is in a workspace you cannot see.',
      reason: 'Reason',
      reference: 'Reference',
    },
  },
  projects: {
    title: 'Projects',
    subtitle: 'Curated sets of a client’s documents, from any of its workspaces, ready for a consumer to pull. Documents are referenced, never copied.',
    create: 'New project',
    columns: { name: 'Project', organization: 'Organization', documents: 'Documents', status: 'Status', created: 'Created' },
    status: { ACTIVE: 'Active', ARCHIVED: 'Archived' },
    emptyTitle: 'No projects yet',
    emptyBody: 'Create a project for a client organization, then add the documents a proof of concept needs.',
    form: { title: 'New project', organization: 'Client organization', chooseOrg: 'Choose an organization', name: 'Name', nameInvalid: 'Use 2 to 120 characters', description: 'Description', submit: 'Create project' },
    createdToast: 'Project “{{name}}” created',
    back: 'All projects',
    documentsTitle: 'Documents',
    documentsIntro: 'Each document stays in its own workspace storage. Only documents marked eligible can be processed by document_fullfilment.',
    eligible: 'Eligible',
    notEligible: 'Not eligible',
    columnsDoc: { file: 'File', workspace: 'Workspace', type: 'Type', size: 'Size', fulfilment: 'Fulfilment', added: 'Added' },
    noDocuments: 'No documents yet. Add some from the organization’s workspaces.',
    add: 'Add documents',
    remove: 'Remove',
    removedToast: 'Removed from the project',
    archive: 'Archive project',
    reactivate: 'Reactivate',
    archivedNotice: 'This project is archived and cannot change.',
    picker: { title: 'Add documents to “{{name}}”', intro: 'Received documents of {{org}}, from every workspace.', selected_one: '{{count}} selected', selected_other: '{{count}} selected', submit: 'Add to project', inProject: 'Already in project', none: 'No received documents match.' },
    addedToast_one: '{{count}} document added',
    addedToast_other: '{{count}} documents added',
    skipped_one: '{{count}} document was not added',
    skipped_other: '{{count}} documents were not added',
    keys: {
      title: 'Consumer access',
      intro: 'Project keys let a developer’s system or an AI agent read this project, and nothing else, through the read API or the MCP server. They are read-only.',
      create: 'Create project key',
      createBody: 'The key can list, describe and download this project’s documents. It cannot change anything.',
      nameHint: 'Who or what will use it, for example “document_fullfilment bridge”.',
      nameInvalid: 'Use 2 to 80 characters',
      submit: 'Create key',
      createdToast: 'Project key created',
      secretTitle: 'Copy the project key now',
      emptyTitle: 'No project keys',
      emptyBody: 'Create a key for each consumer, so each can be revoked on its own.',
      revokeTitle: 'Revoke {{name}}?',
      revokeBody: 'The consumer using this key loses access immediately. Download links already issued expire within minutes.',
      revokeConfirm: 'Revoke key',
      revokedToast: 'Project key revoked',
      quickstartTitle: 'Read API and MCP quickstart',
      mcpBody: 'AI agents connect to the MCP server with the same key. Tools: list_projects, list_documents, get_document, get_document_download_url, list_changes.',
      quickstartBody: 'List the documents, follow changes with the cursor, and request a short-lived download link per document. Every read is audited.',
    },
  },
  workspaces: {
    switcher: { title: 'Workspace', label: 'Workspace: {{name}}. Change', manage: 'Manage workspaces' },
    title: 'Workspaces',
    subtitle: 'Each workspace keeps its documents physically apart from every other workspace — its own storage, its own SFTP endpoint, its own keys.',
    create: 'New workspace',
    columns: { name: 'Workspace', status: 'Status', members: 'People', sftp: 'SFTP endpoint' },
    default: 'Default',
    membersCount_one: '{{count}} person',
    membersCount_other: '{{count}} people',
    adminsSeeAll: 'Organization admins can use every workspace.',
    noSftp: 'Not set up',
    actions: { members: 'People', rename: 'Rename', archive: 'Archive', retry: 'Retry' },
    form: { title: 'New workspace', renameTitle: 'Rename workspace', name: 'Name', nameHint: 'For example a department or a processor, such as “Finance EU”.', nameInvalid: 'Use 2 to 80 characters', submit: 'Create workspace', save: 'Save' },
    createdToast: 'Workspace “{{name}}” is being set up',
    archiveConfirm: { title: 'Archive “{{name}}”?', body: 'Its people, keys and SFTP access stop working. Its documents are kept until Sastrex deletes them.', confirm: 'Archive workspace' },
    membersDialog: { title: 'People in “{{name}}”', body: 'Members of your organization who can send to and see this workspace.', none: 'No members to assign yet. Invite people from Organization settings.', admin: 'Admin', save: 'Save people', savedToast: 'People updated' },
    picker: { label: 'Workspace', placeholder: 'Choose a workspace', hint: 'Documents go only to this workspace.' },
    noAccess: { title: 'You are not in a workspace yet', body: 'Ask an administrator of your organization to add you to a workspace. Once added, you can send documents here.', checkAgain: 'Check again' },
    setup: {
      title_one: 'Setting up your workspace',
      title_other: 'Setting up {{count}} workspaces',
      body: 'You can send documents as soon as it is ready. This page updates by itself.',
      slowTitle_one: 'Your workspace is taking longer than usual',
      slowTitle_other: 'Your workspaces are taking longer than usual',
      slowBody: 'It is still being set up. If it is not ready in a few minutes, contact Sastrex.',
      failedTitle_one: 'Your workspace could not be set up',
      failedTitle_other: 'Your workspaces could not be set up',
      failedBody: 'An administrator can retry it from Workspaces, or contact Sastrex.',
      elapsed: 'for {{time}}',
      checked: 'Checked {{seconds}} s ago',
      checkingNow: 'Checking now…',
    },
    column: 'Workspace',
    allWorkspaces: 'All workspaces',
  },
  upload: {
    title: 'Upload documents',
    subtitle: 'Drop files or whole folders. Each file goes straight to secure storage and is checked on arrival.',
    dropTitle: 'Drop files or folders here',
    dropActive: 'Release to add',
    dropHint: 'PDF, images, Word (.doc, .docx), Excel, PowerPoint, CSV, text, JSON, XML or ZIP · up to {{max}} per file',
    chooseFiles: 'Choose files',
    chooseFolder: 'Choose folder',
    unpackArchives: 'Unpack ZIP files into their documents',
    unpackHint: 'Each document inside a ZIP is sent on its own and listed under the ZIP’s name as its folder. Turn off to send a ZIP as one file.',
    unpacking: 'Unpacking {{name}}…',
    archive: {
      kept_whole: '{{name}} is too large to unpack here (over {{max}}) and will be sent as one file.',
      too_large_to_send: '{{name}} is larger than {{max}} and cannot be sent. Unpack it on your computer and drop the documents instead.',
      no_documents: '{{name}} contains no documents.',
      unreadable_drop: 'Could not read {{name}}. It may have been moved or removed since you chose it; drop it again.',
      added: '{{added}} documents added from the ZIP, {{problems}} need attention',
    },
    batchLabel: 'Batch name',
    batchLabelHint: 'Shown to the Sastrex team next to these files.',
    defaultLabel: 'Upload {{date}}',
    start_one: 'Upload {{formatted}} file',
    start_other: 'Upload {{formatted}} files',
    cancelAll: 'Cancel all',
    clear: 'Clear list',
    newBatch: 'Start a new upload',
    cancelFile: 'Cancel {{name}}',
    removeFile: 'Remove {{name}}',
    queued_one: '{{formatted}} file ready · {{size}}',
    queued_other: '{{formatted}} files ready · {{size}}',
    progress: '{{percent}} of {{size}}',
    eligible: 'Ready for processing',
    notEligible: 'Stored, not auto-processed',
    summary: {
      dismiss: 'Dismiss',
      title: 'Upload finished',
      running: 'Uploading · {{percent}}',
      finishedProblems_one: 'Finished · {{count}} file needs attention',
      finishedProblems_other: 'Finished · {{count}} files need attention',
      nothingReceived: 'Nothing was received',
      files: '{{done}} of {{total}} files',
      bytes: '{{done}} of {{total}}',
      eta: 'about {{time}} left',
      etaUnknown: 'estimating time left',
      filterLabel: 'Show only',
      retry_one: 'Retry {{count}} file',
      retry_other: 'Retry {{count}} files',
      download: 'Download results (CSV)',
      body: '{{uploaded}} received · {{rejected}} rejected · {{failed}} failed or cancelled · {{skipped}} not sent',
      batchOpen: 'This batch stays open so you can retry the files that failed. Starting a new upload makes a new batch.',
      toast_one: '{{formatted}} file received',
      toast_other: '{{formatted}} files received',
      viewHistory: 'See the files',
    },
    groups: {
      attention: 'Needs attention',
      active: 'In progress',
      waiting: 'Waiting',
      done: 'Received',
      cancelled: 'Cancelled',
    },
    list: {
      label_one: '{{count}} file in this batch',
      label_other: '{{count}} files in this batch',
      showAll: 'Show {{count}} more',
      showLess: 'Show fewer',
    },
    live: {
      progress: 'Upload {{percent}} complete',
      finished: 'Upload finished: {{uploaded}} received, {{problems}} need attention',
    },
    results: {
      file: 'File',
      size: 'Bytes',
      status: 'Status',
      reason: 'Reason',
      id: 'Upload id',
    },
    leaveTitle: 'Leave while files are uploading?',
    leaveBody: 'Uploads in progress will stop. Files already received are kept.',
    leaveConfirm: 'Leave and stop uploads',
    leaveCancel: 'Stay',
    reason: {
      unsupported_file_type: 'File type not allowed',
      payload_too_large: 'File is larger than the limit',
      empty_file: 'File is empty',
      name_too_long: 'File name is too long',
      system_file: 'System file, skipped',
      network_error: 'Connection lost',
      validation_failed: 'Rejected by the server',
      size_mismatch: 'Size did not match',
      content_does_not_match_extension: 'Content does not match its extension',
      extension_not_allowed: 'File type not allowed',
      checksum_mismatch: 'Checksum did not match',
      expired: 'Upload link expired',
      storage: 'Storage refused the file',
      rate_limited: 'Server busy, gave up after several tries',
      tenant_unavailable: 'Workspace storage did not answer',
      invalid_state: 'This batch is closed',
      org_suspended: 'Your organization is suspended',
      no_tenant_access: 'You are not in this workspace',
      request_failed: 'The request did not go through',
      not_found: 'Not found, or already deleted',
      tenant_archived: 'Its workspace is archived',
      internal_error: 'Server error',
      nested_archive: 'ZIP inside a ZIP, skipped',
      encrypted_entry: 'Password-protected in the ZIP, skipped',
      unsupported_compression: 'ZIP entry uses an unsupported compression',
      archive_unreadable: 'Could not be read from the ZIP (damaged)',
      other: 'Could not be sent',
    },
  },
  apiKeys: {
    title: 'API keys',
    subtitle: 'Let your systems send documents directly. Each key belongs to this organization.',
    create: 'Create API key',
    columns: { name: 'Name', key: 'Key', scopes: 'Permissions', lastUsed: 'Last used', created: 'Created', status: 'Status' },
    revoked: 'Revoked',
    active: 'Active',
    emptyTitle: 'No API keys',
    emptyBody: 'Create a key to send documents from your own systems.',
    form: {
      title: 'Create API key',
      name: 'Name',
      namePlaceholder: 'ERP connector',
      nameRequired: 'Enter a name',
      scopes: 'Permissions',
      scopesRequired: 'Choose at least one permission',
      submit: 'Create key',
    },
    scope: { 'uploads:write': 'Send documents', 'uploads:read': 'Read upload status' },
    secret: {
      title: 'Copy your API key now',
      body: 'This is the only time the full key is shown. Store it in your secret manager.',
      done: 'I have stored the key',
      replayed: 'The key was already created, but its secret can only be shown once. Revoke it and create a new one.',
    },
    revokeTitle: 'Revoke {{name}}?',
    revokeBody: 'Systems using this key stop working immediately. This cannot be undone.',
    revokeConfirm: 'Revoke key',
    revokedToast: 'API key revoked',
    createdToast: 'API key created',
    quickstart: { title: 'Quickstart', body: 'Register the file, send the bytes to the returned URL, then mark it complete.' },
  },
  sftp: {
    title: 'SFTP',
    subtitle: 'Send files from scheduled jobs or desktop clients. Sign-in uses SSH keys only.',
    connection: 'Connection details',
    host: 'Host',
    port: 'Port',
    username: 'Username',
    folder: 'Folder',
    add: 'Add SSH key',
    columns: { label: 'Label', username: 'Username', fingerprint: 'Fingerprint', lastLogin: 'Last sign-in', created: 'Added' },
    emptyTitle: 'No SFTP access yet',
    emptyBody: 'Add an SSH public key to get a username for this organization.',
    form: {
      title: 'Add SSH key',
      label: 'Label',
      labelPlaceholder: 'Nightly export',
      labelRequired: 'Enter a label',
      publicKey: 'Public key',
      publicKeyHint: 'Paste the .pub file contents. Accepted: ssh-ed25519, ecdsa, or ssh-rsa of at least 3072 bits.',
      publicKeyInvalid: 'This does not look like an SSH public key',
      submit: 'Add key',
    },
    revokeTitle: 'Revoke SFTP access for {{name}}?',
    revokeBody: 'The next sign-in with this key will be refused. Files already sent are kept.',
    revokeConfirm: 'Revoke access',
    revokedToast: 'SFTP access revoked',
    createdToast: 'SSH key added',
    howTo: { title: 'Connect with your client', cli: 'Command line', winscp: 'WinSCP', rclone: 'rclone', winscpBody: 'New site → SFTP → host, port and username above → Advanced → SSH → Authentication → private key file.' },
  },
  admin: {
    banner: 'Sastrex admin: you are viewing every client organization.',
    orgs: {
      title: 'Client organizations',
      subtitle: 'Create a workspace for each client and invite their first administrator.',
      create: 'New organization',
      columns: { name: 'Organization', status: 'Status', uploads: 'Uploads', volume: 'Volume', lastUpload: 'Last upload', created: 'Created' },
      emptyTitle: 'No client organizations',
      emptyBody: 'Create one to invite a client.',
      suspend: 'Suspend',
      activate: 'Activate',
      suspendTitle: 'Suspend {{name}}?',
      suspendBody: 'Members, API keys and SFTP sign-ins are refused until you activate it again. Stored files are kept.',
      suspendConfirm: 'Suspend organization',
      statusToast: 'Organization updated',
      form: {
        title: 'New client organization',
        name: 'Organization name',
        nameRequired: 'Enter a name of 2 to 100 characters',
        adminEmail: 'Client administrator email',
        adminEmailHint: 'They receive an invitation to join as administrator.',
        emailInvalid: 'Enter a valid email address',
        submit: 'Create and invite',
      },
      createdToast: '{{name}} created. Invitation sent.',
    },
    orgDetail: {
      back: 'All client organizations',
      sections: { workspaces: 'Workspaces', people: 'People', documents: 'Documents' },
      openFiles: 'Open in File Sync',
      workspacesIntro: 'Each workspace has its own storage, SFTP endpoint and keys. The client’s admins can manage them too.',
      peopleIntro: 'Members are managed by the client in Organization settings. Sastrex can invite, resend and revoke invitations here.',
      adminAccepted: 'Client administrator has joined',
      adminPending: 'Waiting for the client administrator to accept',
      members: 'Members',
      invitations: 'Pending invitations',
      noMembers: 'Nobody has joined yet.',
      noInvitations: 'No pending invitations.',
      // No ':' in keys — i18next reads it as a namespace separator.
      role: { admin: 'Administrator', member: 'Member' },
      invite: 'Invite someone',
      inviteForm: { title: 'Invite to {{name}}', email: 'Email', role: 'Role', submit: 'Send invitation' },
      invitedToast: 'Invitation sent',
      resend: 'Resend',
      resentToast: 'Invitation sent again',
      revoke: 'Revoke',
      revokeConfirm: { title: 'Revoke the invitation for {{email}}?', body: 'The link in their email stops working.', confirm: 'Revoke invitation' },
      revokedToast: 'Invitation revoked',
      sent: 'Sent {{date}}',
      joined: 'Joined {{date}}',
      documentsIntro: 'Find, preview and download this organization’s files, or send files to it, in File Sync.',
    },
    uploads: {
      title: 'All uploads',
      subtitle: 'Files received from every client organization.',
      orgFilter: 'Organization',
    },
  },
} as const

type Widen<T> = { -readonly [K in keyof T]: T[K] extends string ? string : Widen<T[K]> }
export type Dictionary = Widen<typeof en>
